import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api-client"
import type { UserRef } from "@/lib/types"

export interface Note {
  id: string
  content: string
  user: UserRef
  created_at: string
  updated_at: string
}

// Map entity types to API resource paths
const RESOURCE_PATHS: Record<string, string> = {
  Company: "companies",
  Contact: "contacts",
  Deal: "deals",
  ServiceTicket: "service_tickets",
}

function notePath(entityType: string, entityId: string): string {
  const resource = RESOURCE_PATHS[entityType] ?? entityType
  return `/api/v1/${resource}/${entityId}/notes`
}

export function useNotes(entityType: string, entityId: string | undefined) {
  return useQuery({
    queryKey: ["notes", entityType, entityId],
    queryFn: () =>
      api.get<{ data: Note[] }>(notePath(entityType, entityId!)),
    enabled: !!entityType && !!entityId,
  })
}

export function useCreateNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      entityType,
      entityId,
      content,
    }: {
      entityType: string
      entityId: string
      content: string
    }) => api.post<{ data: Note }>(notePath(entityType, entityId), { content }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: ["notes", variables.entityType, variables.entityId],
      })
      qc.invalidateQueries({
        queryKey: ["entity_related", variables.entityType, variables.entityId],
      })
    },
  })
}

export function useUpdateNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      entityType,
      entityId,
      noteId,
      content,
    }: {
      entityType: string
      entityId: string
      noteId: string
      content: string
    }) =>
      api.patch<{ data: Note }>(`${notePath(entityType, entityId)}/${noteId}`, {
        content,
      }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: ["notes", variables.entityType, variables.entityId],
      })
    },
  })
}

export function useDeleteNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      entityType,
      entityId,
      noteId,
    }: {
      entityType: string
      entityId: string
      noteId: string
    }) => api.delete(`${notePath(entityType, entityId)}/${noteId}`),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: ["notes", variables.entityType, variables.entityId],
      })
      qc.invalidateQueries({
        queryKey: ["entity_related", variables.entityType, variables.entityId],
      })
    },
  })
}
